/**
 * Author Persona Page — UX-8
 *
 * Two-tab pill toggle:
 *   - "✨ Generate with AI" — existing AI flow + Tone selector
 *   - "✍️ Fill Manually" — new form: pen name, bio, writing style, genres, avatar upload
 *
 * Persona list below both tabs:
 *   - Edit / Delete / Use per row
 *   - Delete: inline red confirm strip (no modal)
 *   - Edit: opens manual form prefilled
 *   - Use: navigates to book creation with persona selected
 *
 * Route: /persona (protected)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import type { AuthorPersona } from '../types/book';
import { Navbar } from '../components/layout/Navbar';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PersonasResponse {
  personas: AuthorPersona[];
}

interface GenerateResponse {
  persona: AuthorPersona;
}

interface SavePersonaResponse {
  persona: AuthorPersona;
}

type TabType = 'ai' | 'manual';

// ─── Constants ────────────────────────────────────────────────────────────────

const PREDEFINED_GENRES = [
  'Literary Fiction',
  'Gothic Horror',
  'Thriller',
  'Mystery',
  'Dark Fantasy',
  'Science Fiction',
  'Romance',
  'Historical Fiction',
  'Self-Help',
  'Cozy Mystery',
  'Paranormal',
];

const TONES = [
  { value: '', label: 'Any tone' },
  { value: 'mysterious', label: 'Mysterious & reclusive' },
  { value: 'academic', label: 'Academic & authoritative' },
  { value: 'warm', label: 'Warm & personal' },
  { value: 'bold', label: 'Bold & provocative' },
];

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'neutral', label: 'Neutral' },
];

// ─── Hooks ────────────────────────────────────────────────────────────────────

function usePersonas() {
  return useQuery({
    queryKey: ['personas'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<PersonasResponse>>('/personas');
      const raw = res.data.data?.personas;
      return Array.isArray(raw) ? raw : [];
    },
  });
}

// ─── Page component ───────────────────────────────────────────────────────────

export function AuthorPersonaPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState<TabType>('ai');

  // Edit state: if not null, we're editing an existing persona in the manual form
  const [editingPersona, setEditingPersona] = useState<AuthorPersona | null>(null);

  const { data: personas = [], isLoading: loadingPersonas } = usePersonas();

  const handleEditPersona = (p: AuthorPersona) => {
    setEditingPersona(p);
    setActiveTab('manual');
  };

  const handleClearEdit = () => {
    setEditingPersona(null);
    setActiveTab('ai');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="authed" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Page title */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">Pen Personas</h1>
          <p className="text-sm text-muted-foreground">
            Create and manage the author identities attached to your books.
          </p>
        </div>

        {/* Path toggle pill */}
        <div className="flex w-fit border border-border rounded-xl overflow-hidden mb-6">
          <button
            type="button"
            onClick={() => { setActiveTab('ai'); setEditingPersona(null); }}
            className={[
              'flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap border-none',
              activeTab === 'ai'
                ? 'bg-primary text-primary-foreground'
                : 'bg-transparent text-muted-foreground hover:bg-white/[0.04] hover:text-foreground',
            ].join(' ')}
          >
            ✨ Generate with AI
          </button>
          <div className="w-px bg-border" />
          <button
            type="button"
            onClick={() => setActiveTab('manual')}
            className={[
              'flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap border-none',
              activeTab === 'manual'
                ? 'bg-primary text-primary-foreground'
                : 'bg-transparent text-muted-foreground hover:bg-white/[0.04] hover:text-foreground',
            ].join(' ')}
          >
            ✍️ Fill Manually
          </button>
        </div>

        {/* Tab content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {activeTab === 'ai' ? (
            <AIGeneratePanel
              personas={personas}
              onInvalidate={() => qc.invalidateQueries({ queryKey: ['personas'] })}
            />
          ) : (
            <ManualFillPanel
              personas={personas}
              editingPersona={editingPersona}
              onClearEdit={handleClearEdit}
              onInvalidate={() => { qc.invalidateQueries({ queryKey: ['personas'] }); }}
            />
          )}

          {/* Right panel: persona list */}
          <div>
            <PersonaList
              personas={personas}
              isLoading={loadingPersonas}
              onEdit={handleEditPersona}
              onInvalidate={() => qc.invalidateQueries({ queryKey: ['personas'] })}
              t={t}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

// ─── AI Generate Panel ────────────────────────────────────────────────────────

interface AIPanelProps {
  personas: AuthorPersona[];
  onInvalidate: () => void;
}

function AIGeneratePanel({ personas, onInvalidate }: AIPanelProps) {
  const [penName, setPenName] = useState('');
  const [genre, setGenre] = useState('');
  const [gender, setGender] = useState('female');
  const [tone, setTone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const generateMutation = useMutation({
    mutationFn: async () => {
      const genreValue = genre.trim() || 'Dark Fantasy / Gothic';
      const toneNote = tone ? ` Tone: ${tone}.` : '';
      const body = {
        penName: penName.trim(),
        genre: `${genreValue} (${gender} pronouns)${toneNote}`,
      };
      const res = await api.post<ApiSuccess<GenerateResponse>>('/personas/generate', body);
      return res.data.data;
    },
    onSuccess: () => {
      onInvalidate();
      setError(null);
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) => {
      setError(err.response?.data?.error?.message ?? 'Generation failed. Please try again.');
    },
  });

  const atLimit = personas.length >= 5;

  return (
    <div className="border border-border rounded-xl bg-card p-5 sm:p-6 h-fit">
      <h2 className="text-base font-bold text-foreground mb-1">Generate with AI</h2>
      <p className="text-xs text-muted-foreground mb-5">
        Answer a few questions and AI builds a complete author persona.
      </p>

      <div className="space-y-4">
        {/* Genre */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-1.5">
            Genre / Vibe
          </label>
          <select
            aria-label="Genre / Vibe"
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground outline-none focus:border-ring appearance-none"
          >
            <option value="">Dark Fantasy / Gothic</option>
            {PREDEFINED_GENRES.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>

        {/* Pen name */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-1.5">
            Pen Name
          </label>
          <input
            type="text"
            value={penName}
            onChange={(e) => setPenName(e.target.value)}
            placeholder="e.g. J.R. Blackwood"
            maxLength={100}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-ring"
          />
        </div>

        {/* Gender expression */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-1.5">
            Gender Expression (for bio language)
          </label>
          <div className="flex gap-1.5 flex-wrap">
            {GENDERS.map((g) => (
              <button
                key={g.value}
                type="button"
                onClick={() => setGender(g.value)}
                className={[
                  'px-3 py-1.5 rounded-md border text-xs font-medium transition-colors',
                  gender === g.value
                    ? 'border-primary bg-primary/12 text-violet-400'
                    : 'border-border bg-transparent text-muted-foreground hover:border-border/80',
                ].join(' ')}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tone */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-1.5">
            Tone (optional)
          </label>
          <select
            aria-label="Writing tone (optional)"
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground outline-none focus:border-ring appearance-none"
          >
            {TONES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="px-3 py-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
            {error}
          </div>
        )}

        {atLimit && (
          <div className="px-3 py-2 bg-amber-900/20 border border-amber-700/30 rounded text-sm text-amber-400">
            You've reached the 5-persona limit. Delete one to generate more.
          </div>
        )}

        <button
          type="button"
          disabled={generateMutation.isPending || atLimit || !penName.trim()}
          onClick={() => generateMutation.mutate()}
          className="w-full py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {generateMutation.isPending ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Generating…
            </>
          ) : (
            '✨ Generate Persona'
          )}
        </button>
      </div>

      {/* Generated result */}
      {generateMutation.isSuccess && generateMutation.data && (
        <div className="mt-5 pt-5 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-green-400 text-sm">✓</span>
            <span className="text-sm font-medium text-foreground">Generated &amp; saved!</span>
          </div>
          <GeneratedPersonaCard persona={generateMutation.data.persona} />
        </div>
      )}
    </div>
  );
}

// ─── Generated Persona Preview ────────────────────────────────────────────────

function GeneratedPersonaCard({ persona }: { persona: AuthorPersona }) {
  const initial = persona.penName.charAt(0).toUpperCase();
  return (
    <div className="bg-primary/[0.06] border border-primary/20 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-primary shrink-0">
          {initial}
        </div>
        <div>
          <div className="text-sm font-bold text-foreground">{persona.penName}</div>
          <div className="text-xs text-muted-foreground">{persona.genreSpecialization}</div>
        </div>
      </div>
      <p className="text-sm text-foreground/80 leading-relaxed mb-3">{persona.bio}</p>
      {persona.funFacts && persona.funFacts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {persona.funFacts.slice(0, 3).map((fact, i) => (
            <span
              key={i}
              className="inline-block px-2 py-0.5 rounded-full bg-primary/15 text-violet-400 text-[10px] font-semibold"
            >
              {fact.length > 40 ? fact.slice(0, 40) + '…' : fact}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Manual Fill Panel ────────────────────────────────────────────────────────

interface ManualPanelProps {
  personas: AuthorPersona[];
  editingPersona: AuthorPersona | null;
  onClearEdit: () => void;
  onInvalidate: () => void;
}

function ManualFillPanel({ personas, editingPersona, onClearEdit, onInvalidate }: ManualPanelProps) {
  const isEditing = editingPersona !== null;

  const [penName, setPenName] = useState(editingPersona?.penName ?? '');
  const [bio, setBio] = useState(editingPersona?.bio ?? '');
  const [writingStyle, setWritingStyle] = useState(editingPersona?.writingStyle ?? '');
  const [selectedGenres, setSelectedGenres] = useState<string[]>(
    editingPersona?.genres ?? [],
  );
  const [customGenreInput, setCustomGenreInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const atLimit = !isEditing && personas.length >= 5;

  // Toggle predefined genre tag
  const toggleGenre = (g: string) => {
    setSelectedGenres((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g],
    );
  };

  // Add custom genre tag on Enter or Add button click
  const addCustomGenre = () => {
    const g = customGenreInput.trim();
    if (g && !selectedGenres.includes(g)) {
      setSelectedGenres((prev) => [...prev, g]);
    }
    setCustomGenreInput('');
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEditing && editingPersona) {
        const res = await api.put<ApiSuccess<SavePersonaResponse>>(
          `/personas/${editingPersona.id}`,
          {
            penName: penName.trim(),
            bio: bio.trim(),
            writingStyle: writingStyle.trim(),
            genres: selectedGenres,
          },
        );
        return res.data.data;
      } else {
        const res = await api.post<ApiSuccess<SavePersonaResponse>>('/personas', {
          manualMode: true as const,
          penName: penName.trim(),
          bio: bio.trim(),
          writingStyle: writingStyle.trim(),
          genres: selectedGenres,
        });
        return res.data.data;
      }
    },
    onSuccess: () => {
      onInvalidate();
      setError(null);
      setSuccess(true);
      if (!isEditing) {
        setPenName('');
        setBio('');
        setWritingStyle('');
        setSelectedGenres([]);
      } else {
        onClearEdit();
      }
      setTimeout(() => setSuccess(false), 3000);
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) => {
      setError(err.response?.data?.error?.message ?? 'Save failed. Please try again.');
    },
  });

  // Live preview initial
  const previewInitial = penName.charAt(0).toUpperCase() || '?';

  return (
    <div className="border border-border rounded-xl bg-card p-5 sm:p-6 h-fit">
      <h2 className="text-base font-bold text-foreground mb-1">
        {isEditing ? `Edit "${editingPersona?.penName}"` : 'Fill Manually'}
      </h2>
      <p className="text-xs text-muted-foreground mb-5">
        {isEditing
          ? "Update this persona's details."
          : 'Build your author persona exactly how you want it.'}
      </p>

      <div className="space-y-4">
        {/* Pen name */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-1.5">
            Pen Name / Author Name
          </label>
          <input
            type="text"
            value={penName}
            onChange={(e) => setPenName(e.target.value)}
            placeholder="e.g. M.A. Coldwell"
            maxLength={100}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-ring"
          />
        </div>

        {/* Short bio */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-1.5">
            Short Bio
          </label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="2–3 sentences about this author…"
            maxLength={2000}
            rows={3}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-ring resize-y min-h-[80px] leading-relaxed"
          />
        </div>

        {/* Writing style */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-1.5">
            Writing Style
          </label>
          <input
            type="text"
            value={writingStyle}
            onChange={(e) => setWritingStyle(e.target.value)}
            placeholder="e.g. lyrical literary fiction, atmospheric slow-burn"
            maxLength={200}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-ring"
          />
        </div>

        {/* Genres */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground mb-1.5">
            Genres (select all that apply)
          </label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {PREDEFINED_GENRES.map((g) => {
              const isSelected = selectedGenres.includes(g);
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGenre(g)}
                  className={[
                    'inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/12 text-violet-400'
                      : 'border-border bg-background text-muted-foreground hover:border-border/80',
                  ].join(' ')}
                >
                  {g}
                  {isSelected && <span className="text-violet-400 leading-none">×</span>}
                </button>
              );
            })}
            {/* Custom genre tags (not in predefined list) */}
            {selectedGenres.filter((g) => !PREDEFINED_GENRES.includes(g)).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => toggleGenre(g)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-primary bg-primary/12 text-violet-400 text-xs transition-colors"
              >
                {g}
                <span className="text-violet-400 leading-none">×</span>
              </button>
            ))}
          </div>
          {/* Custom genre input */}
          <div className="flex gap-2 mt-1">
            <input
              type="text"
              value={customGenreInput}
              onChange={(e) => setCustomGenreInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomGenre(); } }}
              placeholder="Add custom genre…"
              maxLength={60}
              className="flex-1 bg-background border border-border rounded-md px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:border-ring"
            />
            <button
              type="button"
              onClick={addCustomGenre}
              disabled={!customGenreInput.trim()}
              className="px-3 py-1.5 rounded-md border border-border bg-transparent text-muted-foreground text-xs hover:border-border/80 hover:text-foreground transition-colors disabled:opacity-40"
            >
              Add
            </button>
          </div>
        </div>

        {/* Live preview */}
        {(penName || bio) && (
          <div className="bg-primary/[0.06] border border-primary/20 rounded-lg p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Preview
            </div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-base font-bold text-primary shrink-0">
                {previewInitial}
              </div>
              <div>
                <div className="text-sm font-bold text-foreground">{penName || 'Author Name'}</div>
                <div className="text-xs text-muted-foreground">
                  {selectedGenres.length > 0 ? selectedGenres.slice(0, 2).join(' / ') : 'Your genres'}
                </div>
              </div>
            </div>
            {bio && (
              <p className="text-sm text-foreground/80 leading-relaxed">{bio}</p>
            )}
          </div>
        )}

        {error && (
          <div className="px-3 py-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
            {error}
          </div>
        )}

        {success && (
          <div className="px-3 py-2 bg-green-500/10 border border-green-500/20 rounded text-sm text-green-400">
            ✓ Persona {isEditing ? 'updated' : 'saved'} successfully!
          </div>
        )}

        {atLimit && (
          <div className="px-3 py-2 bg-amber-900/20 border border-amber-700/30 rounded text-sm text-amber-400">
            You've reached the 5-persona limit. Delete one to add more.
          </div>
        )}

        <button
          type="button"
          disabled={saveMutation.isPending || atLimit || !penName.trim() || !bio.trim()}
          onClick={() => saveMutation.mutate()}
          className="w-full py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saveMutation.isPending ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Saving…
            </>
          ) : isEditing ? (
            '💾 Update Persona'
          ) : (
            '💾 Save Persona'
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Persona List ─────────────────────────────────────────────────────────────

interface PersonaListProps {
  personas: AuthorPersona[];
  isLoading: boolean;
  onEdit: (p: AuthorPersona) => void;
  onInvalidate: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function PersonaList({ personas, isLoading, onEdit, onInvalidate, t }: PersonaListProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (personaId: string) => {
      await api.delete(`/personas/${personaId}`);
    },
    onSuccess: () => {
      onInvalidate();
      setConfirmDeleteId(null);
      setDeletingId(null);
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const handleDeleteClick = (id: string) => {
    setConfirmDeleteId(id);
  };

  const handleConfirmDelete = (id: string) => {
    setDeletingId(id);
    deleteMutation.mutate(id);
  };

  const AVATAR_COLORS: Record<number, { bg: string; text: string }> = {
    0: { bg: 'rgba(124,58,237,0.15)', text: '#a78bfa' },
    1: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
    2: { bg: 'rgba(236,72,153,0.15)', text: '#f472b6' },
    3: { bg: 'rgba(16,185,129,0.15)', text: '#34d399' },
    4: { bg: 'rgba(245,158,11,0.15)', text: '#fbbf24' },
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (personas.length === 0) {
    return (
      <div className="border border-dashed border-border rounded-xl p-10 text-center">
        <div className="text-4xl mb-3">✍️</div>
        <p className="text-sm font-semibold text-foreground mb-1">No personas yet</p>
        <p className="text-xs text-muted-foreground">
          Generate or fill one to get started.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-foreground">
          My Personas{' '}
          <span className="text-sm font-normal text-muted-foreground">
            ({personas.length} of 5 saved)
          </span>
        </h2>
      </div>

      <div className="space-y-2">
        {personas.map((p, i) => {
          const colors = AVATAR_COLORS[i % 5] ?? AVATAR_COLORS[0];
          const initial = p.penName.charAt(0).toUpperCase();
          const isConfirming = confirmDeleteId === p.id;
          const isDeleting = deletingId === p.id;

          return (
            <div key={p.id}>
              <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3 hover:border-primary/35 transition-colors">
                <div
                  className="w-9.5 h-9.5 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                  style={{ width: 38, height: 38, background: colors.bg, color: colors.text }}
                >
                  {initial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-foreground truncate">{p.penName}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {p.genreSpecialization}
                    {p.manualMode ? ' · Manually created' : ' · AI-generated'}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => onEdit(p)}
                    className="px-2.5 py-1 rounded border border-border bg-transparent text-muted-foreground text-[11px] hover:border-border/80 hover:text-foreground transition-colors"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(p.id)}
                    className="px-2.5 py-1 rounded border border-red-500/25 bg-transparent text-red-400 text-[11px] hover:bg-red-500/[0.08] transition-colors"
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>

              {/* Inline delete confirm strip */}
              {isConfirming && (
                <div className="bg-red-500/10 border border-red-500/25 rounded-lg px-4 py-3 flex items-center justify-between gap-2 flex-wrap mt-1">
                  <span className="text-[13px] text-red-300">
                    ⚠️ Delete &ldquo;{p.penName}&rdquo;? This can&apos;t be undone.
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      disabled={isDeleting}
                      className="px-3 py-1.5 rounded border border-border bg-transparent text-sm text-foreground hover:bg-white/[0.04] transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleConfirmDelete(p.id)}
                      disabled={isDeleting}
                      className="px-3 py-1.5 rounded border-none bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                      {isDeleting ? (
                        <>
                          <span className="h-3 w-3 animate-spin rounded-full border border-current border-t-transparent" />
                          Deleting…
                        </>
                      ) : (
                        t('persona.delete', 'Delete')
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add more slot */}
        {personas.length < 5 && (
          <div className="border border-dashed border-white/10 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">
              {5 - personas.length} more slot{5 - personas.length !== 1 ? 's' : ''} available (max 5)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
